import { WorkerStage } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import type { ClipboardScoreHistoryResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import type { ClipboardScoreProfileResponse } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreProfile";
import { ON_TIME_BONUS_GRACE_PERIOD_MINUTES } from "@src/appV2/ClipboardScore/constants";
import type { WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { differenceInHours, differenceInMinutes, min, parseISO } from "date-fns";
import { useMemo } from "react";

import { calculateClipboardScoreChange } from "../utils/calculateClipboardScoreChange";

interface UseClipboardScoreDataParams {
  clipboardScoreHistory: ClipboardScoreHistoryResponse | undefined;
  clipboardScoreProfile: ClipboardScoreProfileResponse | undefined;
  shift: WorkerShift | undefined;
  discardLatenessBooktimeThresholdInHours: number;
}

interface ClipboardScoreData {
  attendanceScoreData: ReturnType<typeof calculateClipboardScoreChange>;
  ranking?: {
    rank: number;
    totalWorkers: number;
    workerType?: string;
  };
  isFirstShiftInDouble: boolean;
  isSecondShiftInDouble: boolean;
  wasLastMinuteBooking: boolean;
  latenessMinutes?: number;
  isAlreadyRestricted: boolean;
  newlyRestricted: boolean;
  isScoreFrozen: boolean;
}

export function useClipboardScoreData(params: UseClipboardScoreDataParams): ClipboardScoreData {
  const {
    clipboardScoreHistory,
    clipboardScoreProfile,
    shift,
    discardLatenessBooktimeThresholdInHours,
  } = params;

  const attendanceScoreData = calculateClipboardScoreChange(clipboardScoreHistory);

  const ranking = useMemo(() => {
    if (!isDefined(clipboardScoreProfile?.clipboardScoreRank)) {
      return undefined;
    }

    return {
      rank: clipboardScoreProfile.clipboardScoreRank.rank,
      totalWorkers: clipboardScoreProfile.clipboardScoreRank.outOf,
      workerType: shift?.agentReq,
    };
  }, [clipboardScoreProfile?.clipboardScoreRank, shift?.agentReq]);

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- We want falsy values (including false) to fall through
  const isSecondShiftInDouble = Boolean(shift?.isAfterAnotherShift || shift?.isSecondShiftInDouble);

  const isFirstShiftInDouble = Boolean(shift?.isBeforeAnotherShift);

  const wasLastMinuteBooking = useMemo(() => {
    if (shift?.urgentlyBooked) {
      return true;
    }

    if (!isDefined(shift?.bookedAt) || !isDefined(shift?.start)) {
      return false;
    }

    const bookingLeadTime = differenceInHours(parseISO(shift.start), parseISO(shift.bookedAt), {
      roundingMethod: "ceil",
    });

    return bookingLeadTime <= discardLatenessBooktimeThresholdInHours;
  }, [shift, discardLatenessBooktimeThresholdInHours]);

  const latenessMinutes = useMemo(() => {
    if (!isDefined(shift?.clockInOut?.start) || !isDefined(shift?.start)) {
      return undefined;
    }

    const scheduledArrivalTime = parseISO(shift.computedArrival?.targetArrivalTime ?? shift.start);

    const arrivalDates = [
      parseISO(shift.clockInOut.start),
      ...(isDefined(shift.geofence?.entry) ? [parseISO(shift.geofence.entry)] : []),
    ];
    const actualStart = min(arrivalDates);
    const minutesDifference = differenceInMinutes(actualStart, scheduledArrivalTime);

    // Account for grace period - only consider late if more than grace period minutes past start
    return minutesDifference > ON_TIME_BONUS_GRACE_PERIOD_MINUTES ? minutesDifference : undefined;
  }, [
    shift?.clockInOut?.start,
    shift?.computedArrival?.targetArrivalTime,
    shift?.start,
    shift?.geofence?.entry,
  ]);

  const isAlreadyRestricted = Boolean(
    clipboardScoreProfile?.accountStatus.status === WorkerStage.RESTRICTED
  );

  const newlyRestricted = useMemo(() => {
    if (isAlreadyRestricted || !attendanceScoreData) {
      return false;
    }

    // Check if the score change resulted in a restriction (score dropped to 0 or below)
    const { previousScore, currentScore } = attendanceScoreData;

    return previousScore > 0 && currentScore <= 0;
  }, [isAlreadyRestricted, attendanceScoreData]);

  const isScoreFrozen = Boolean(clipboardScoreProfile?.scoreFreeze.enabled);

  return {
    attendanceScoreData,
    ranking,
    isFirstShiftInDouble,
    isSecondShiftInDouble,
    wasLastMinuteBooking,
    latenessMinutes,
    isAlreadyRestricted,
    newlyRestricted,
    isScoreFrozen,
  };
}
